import { DOOR_REASONS } from './analytics';

/**
 * A chart's doors as links (plan §5.4, invariant I2): every count opens the
 * items list filtered to exactly the items behind it.
 *
 * The SERVER builds the whole door — the chart's card, its filter, its range
 * and the bucket, as one KQL query — and the web only carries it to the list.
 * The web never builds or joins KQL (a joined `<filter> and <clause>` loses
 * precedence; knowledge/lessons/doors-to-a-filtered-list.md).
 *
 * This module sits OUTSIDE the charts chunk (src/components/charts/, behind
 * LazyCharts.js) so a page that never draws a chart — the Reports list, the
 * chart page's ⋯ — can open a chart's list by the same rule the renderer
 * uses. `components/charts/chartDoors.js` and `chartResult.js` read it from
 * here, so there is one door URL and one reading of the whole chart's door.
 */

/** Said where a mark has no door and the result gives no reason for it. */
export const NO_DOOR_GENERIC = "This part of the chart can't open a list.";

/** The sentence for a no-door reason code, from the vendored capability table, else the run's own words. */
export function doorReasonText(reason, message) {
	if (typeof reason === 'string' && Object.prototype.hasOwnProperty.call(DOOR_REASONS, reason)) return DOOR_REASONS[reason];
	if (typeof message === 'string' && message.trim()) return message.trim();
	return null;
}

/**
 * `/collections/:id/items?linked=0&q=<door>`, or null when there is no door.
 *
 * The URL always says `linked=0`: the list turns linked records on by default
 * where a collection has incoming links, and a chart never counts them. The
 * card is inside the door's `q` (`card = <id> and …`), never `?card=`, which
 * the list sends without descendants.
 */
export function chartDoorUrl(collectionId, door) {
	const id = typeof collectionId === 'string' ? collectionId.trim() : '';
	if (!/^[A-Za-z0-9_-]+$/.test(id)) return null;
	if (typeof door !== 'string' || !door.trim()) return null;
	const params = new URLSearchParams({ linked: '0', q: door });
	return `/collections/${id}/items?${params.toString()}`;
}

const doorText = (value) => (typeof value === 'string' && value.trim() ? value : null);

// The first no-door note at a path, as its sentence: at `time` (the chart's
// own date) either code closes every door; at `splitBy` only `no_door` speaks
// for the whole chart (`no_door_other` is the folded Other's).
function noDoorAt(notes, prefix) {
	const codes = prefix === 'time' ? ['no_door', 'no_door_other'] : ['no_door'];
	const note = (Array.isArray(notes) ? notes : []).find((n) => n && codes.includes(n.code) && typeof n.path === 'string' && n.path.startsWith(prefix));
	return note ? (doorReasonText(note.reason, note.message) || NO_DOOR_GENERIC) : null;
}

/**
 * The door of the WHOLE chart — every item it counts — as { door, reason }.
 *
 * `doors.chart` (keepr-api SCHEMA "Charts", settled by A2a) for a split chart
 * and an unsplit one alike. A result without the key (A4's recordings) falls
 * back to an unsplit chart's one series; a split chart then has none. A chart
 * date with no door (a `no_door` note at `time`) closes it whatever the
 * result holds; a null door says why where the run did (its notes put the
 * whole chart's at `splitBy`). `split` says whether the chart is split (the
 * spec's `splitBy`); without it, more than one series reads as split.
 */
export function wholeChartDoor(result, { split } = {}) {
	if (!result || result.ok !== true) return { door: null, reason: null };
	const doors = result.doors && typeof result.doors === 'object' ? result.doors : {};
	const notes = result.notes;
	const blocked = noDoorAt(notes, 'time');
	if (blocked) return { door: null, reason: blocked };
	const series = Array.isArray(result.series) ? result.series : [];
	const isSplit = typeof split === 'boolean' ? split : series.length > 1;
	const splitReason = () => noDoorAt(notes, 'splitBy') || NO_DOOR_GENERIC;
	if (Object.prototype.hasOwnProperty.call(doors, 'chart')) {
		const text = doorText(doors.chart);
		return text ? { door: text, reason: null } : { door: null, reason: splitReason() };
	}
	if (isSplit) return { door: null, reason: splitReason() };
	const one = Array.isArray(doors.series) ? doorText(doors.series[0]) : null;
	return one ? { door: one, reason: null } : { door: null, reason: splitReason() };
}
